<template>
  <div class="orgao">
    <div class="centro">
      <h1>Consulta por Órgãos</h1>
      <BuscaSimplesCards
        titulo="Consultas Rápidas"
        :cards="cardsAtoInfralegal"
      />
      <Loading v-if="carregando" />
      <ErroResultados v-if="!carregando && erro" />
      <PainelBuscaSimplificada
        :dados="orgaosFormatados"
        :generateUrl="generateUrl"
        titulo="Órgãos relacionados"
        v-if="!carregando && !erro"
      />
    </div>
  </div>
</template>

<script>
import AtoInfralegalApi from "../../services/AtoInfralegalApi";
import Loading from "../../components/Loading";
import ErroResultados from "../../components/ErroResultados";
import PainelBuscaSimplificada from "../../components/PainelBuscaSimplificada";
import { mixinAtoInfralegal } from "../../mixins/mixinAtoInfralegal";
// Não precisamos do slugify aqui, pois o slug virá do backend
// import slugify from "slugify"; 

import metaDados from "../../helpers/metaDados";

export default {
  name: "PorOrgaoAtoInfralegal",
  metaInfo() {
    return {
      title: "Pesquisa de Ato Infralegal por Órgãos",
      titleTemplate: "%s - Casa Civil do Estado de Goiás",
      meta: [
        {
          vmid: "description",
          name: "description",
          property: "description",
          content:
            "Página de acesso e pesquisa a toda legislação e atos normativos do Governo do Estado de Goiás.",
        },
        {
          vmid: "og:description",
          property: "og:description",
          content:
            "Página de acesso e pesquisa a toda legislação e atos normativos do Governo do Estado de Goiás.",
        },
        {
          vmid: "og:title",
          property: "og:title",
          content:
            "Pesquisa o Ato Infralegal por Órgão - Casa Civil do Estado de Goiás",
        },
        {
          vmid: "og:type",
          property: "og:type",
          content: "article",
        },
        {
          vmid: "twitter:description",
          name: "twitter:description",
          property: "twitter:description",
          content:
            "Página de acesso e pesquisa a toda legislação e atos normativos do Governo do Estado de Goiás.",
        },
        {
          vmid: "twitter:title",
          name: "twitter:title",
          property: "twitter:title",
          content:
            "Pesquisa o Ato Infralegal por Órgão - Casa Civil do Estado de Goiás",
        },
        ...metaDados(this.$route),
      ],
    };
  },
  mixins: [mixinAtoInfralegal],
  data() {
    return {
      carregando: true,
      erro: false,
      orgaos: [], // Esta será a lista final combinada
      filtro: {
        orgao_emissor: [],
        setor: null,
        ano: null,
      },
    };
  },
  computed: {
    orgaosFormatados() {
      // Aqui, orgaos já deve estar combinado no carregarOrgaos
      return this.orgaos.map((orgao) => {
        const slug = orgao.slug || 'orgao-desconhecido';
        return {
          ...orgao,
          // 'slug' já virá da combinação, 'quantidade_total' e 'anos_total' também.
          // Apenas garantimos um fallback para slug, caso algo dê errado na API.
          slug,
          quantidade_total: orgao.quantidade_total || 0,
          anos_total: orgao.anos_total || [],
          // Segundo nível: setores (Unidade Responsável) do órgão. Os anos do
          // órgão continuam nos botões acima deles. Cada setor guarda o órgão
          // pai para montar a URL de pesquisa.
          // Sem 'quantidade', o painel não exibe o card "Outras".
          children: (orgao.children || []).map((setor) => ({
            ...setor,
            orgao_id: orgao.id,
            orgao_slug: slug,
          })),
        };
      });
    },
  },
  components: {
    PainelBuscaSimplificada,
    Loading,
    ErroResultados,
    // BuscaSimplesCards,
  },
  created() {
    this.atoInfralegalApi = new AtoInfralegalApi();
    this.carregarOrgaos();
  },
  methods: {
    carregarOrgaos: async function () {
      try {
        this.carregando = true;
        this.erro = false;

        // 1. Buscar os órgãos com SLUG do novo endpoint
        if (this.atoInfralegalApi.cancelTokens.getOrgaosComSlug) { 
          this.atoInfralegalApi.cancelTokens.getOrgaosComSlug.cancel(); 
        }
        const orgaosComSlug = await this.atoInfralegalApi.getOrgaosComSlug();

        // 2. Buscar os órgãos com QUANTIDADE e ANOS do endpoint antigo
        if (this.atoInfralegalApi.cancelTokens.getOrgaos) { 
          this.atoInfralegalApi.cancelTokens.getOrgaos.cancel(); 
        }
        const orgaosComDadosCompletos = await this.atoInfralegalApi.getOrgaos();
        
        // Converter orgaosComDadosCompletos para um mapa para fácil lookup por ID
        const orgaosMap = orgaosComDadosCompletos.reduce((map, orgao) => {
          map[orgao.id] = orgao;
          return map;
        }, {});

        // 3. Combinar os resultados
        this.orgaos = orgaosComSlug.map(orgaoSlug => {
          const dadosCompletos = orgaosMap[orgaoSlug.id];
          if (dadosCompletos) {
            return {
              ...dadosCompletos, // Pega nome, sigla, quantidade, anos_total
              slug: orgaoSlug.slug // Sobrescreve/adiciona o slug do outro endpoint
            };
          }
          // Caso um órgão com slug não tenha dados completos (improvável, mas para robustez)
          return {
            id: orgaoSlug.id,
            nome: orgaoSlug.nome, // O nome também vem no endpoint de slug
            sigla: orgaoSlug.sigla, // A sigla também vem no endpoint de slug
            slug: orgaoSlug.slug,
            quantidade_total: 0,
            anos_total: []
          };
        }).sort((a, b) => a.nome.localeCompare(b.nome)); // Opcional: ordenar pelo nome

        this.carregando = false;
      } catch (err) {
        if (this.atoInfralegalApi.isCancel(err)) {
          return;
        }
        console.error("Erro ao carregar e combinar dados de órgãos:", err); 
        this.erro = true;
        this.carregando = false;
      }
    },
    
    // A função gerarOrgaoSlugParaLink não é mais necessária aqui, pois o slug virá do backend.
    /*
    gerarOrgaoSlugParaLink: function(orgao) {
      // ... (código antigo removido)
    },
    */

    generateUrl(ano, dados) {
      // Setor (2º nível) carrega o órgão pai em orgao_id/orgao_slug.
      if (dados.orgao_id) {
        this.pesquisar(ano, dados.orgao_id, dados.orgao_slug, dados.id);
      } else {
        this.pesquisar(ano, dados.id, dados.slug, null);
      }
    },
    pesquisar(ano, orgaoId, orgaoSlug, setor) {
      this.filtro.orgao_emissor = [orgaoId];
      this.filtro.setor = setor;
      this.filtro.ano = ano;

      this.$router.push({
        name: "PesquisaAtoInfralegal",
        params: { orgaoSlug: orgaoSlug },
        query: {
          ...this.filtro,
          pagina: 1,
          qtd_por_pagina: this.qtd_por_pagina,
          busca_avancada: 1,
        },
      });
    },
    formatNumber(number) {
      if (number) return number.toLocaleString("pt-BR");
    },
  },
};
</script>

<style lang="scss" scoped>
h1 {
  background-color: #ffb24e;
  width: fit-content;
  border-radius: 12px;
  color: #115810;
  font-size: 1.5rem;
  padding: 0 0.3rem;
  margin: 2rem 0rem 1.5rem 0.5rem;
}
.orgao {
  min-height: calc(100vh - 10rem);
  margin-bottom: 5rem;
}
</style>
